/** Customer statement of account (M04-13). */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { amount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { manilaToday, useContacts } from '../../lib/queries';
import { Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, TableWrap, Td, Th, filterClass } from '../../components/ui';

interface Statement {
  customer: { id: string; code: string; name: string };
  opening: string;
  closing: string;
  lines: { date: string; kind: string; number: string; charge: string; credit: string; balance: string; note: string; id: string }[];
}

const KIND: Record<string, string> = {
  SALES_INVOICE: 'Invoice',
  DEBIT_MEMO: 'Debit memo',
  CREDIT_MEMO: 'Credit memo',
  RECEIPT: 'Payment',
  CANCELLATION: 'Cancelled',
  RECEIPT_VOID: 'Payment voided',
};

export function CustomerStatementPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const customers = useContacts('customer');
  const today = manilaToday();
  const [contactId, setContactId] = useState(params.get('contact') ?? '');
  const [from, setFrom] = useState(`${today.slice(0, 4)}-01-01`);
  const [to, setTo] = useState(today);
  const soa = useQuery({ queryKey: ['soa', contactId, from, to], queryFn: () => api<Statement>(`/reports/customer-statement/${contactId}?from=${from}&to=${to}`), enabled: Boolean(contactId) });
  return (
    <>
      <PageHeader title="Statement of Account" description="Every invoice, memo and payment for one customer, with the running balance." />
      <FilterBar>
        <select className={`${filterClass} w-72`} value={contactId} onChange={(e) => { setContactId(e.target.value); setParams({ contact: e.target.value }, { replace: true }); }} aria-label="Customer">
          <option value="">Choose a customer</option>
          {(customers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.registeredName}</option>)}
        </select>
        <label className="text-sm text-slate-600 dark:text-slate-300">From <input type="date" className={`${filterClass} ml-1`} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-sm text-slate-600 dark:text-slate-300">To <input type="date" className={`${filterClass} ml-1`} value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </FilterBar>
      <Card>
        {!contactId && <EmptyState title="Choose a customer" />}
        {soa.isLoading && <Skeleton className="h-48 w-full" />}
        {soa.data && (
          <>
            <div className="mb-4 flex flex-wrap gap-6 text-sm">
              <span><span className="text-slate-500">Opening</span> <Money className="font-semibold">{amount(soa.data.opening)}</Money></span>
              <span><span className="text-slate-500">Balance</span> <Money className="font-semibold">{amount(soa.data.closing)}</Money></span>
            </div>
            {soa.data.lines.length === 0 ? <EmptyState title="No activity in this range" /> : (
              <TableWrap>
                <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Date</Th><Th>Document</Th><Th>Number</Th><Th align="right">Charges</Th><Th align="right">Credits</Th><Th align="right">Balance</Th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {soa.data.lines.map((l, i) => (
                    <tr key={`${l.id}-${i}`}>
                      <Td className="whitespace-nowrap">{shortDate(l.date)}</Td>
                      <Td>{KIND[l.kind] ?? l.kind}{l.note && <span className="block text-xs text-slate-500">{l.note}</span>}</Td>
                      <Td className="whitespace-nowrap"><Link className="text-brand-700 hover:underline dark:text-brand-100" to={l.kind.startsWith('RECEIPT') ? `/receipts/${l.id}` : `/sales/${l.id}`}>{l.number}</Link></Td>
                      <Td align="right"><Money>{amount(l.charge, true)}</Money></Td>
                      <Td align="right"><Money>{amount(l.credit, true)}</Money></Td>
                      <Td align="right"><Money>{amount(l.balance)}</Money></Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </>
        )}
      </Card>
    </>
  );
}
